import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { getBookings, syncCalendar, getSyncStatus, getVehicleBlocks, type VehicleBlock, getVehicles, getFleetEvents, searchVehicles, type Booking, type FleetEvent } from '../api';
import type { Vehicle } from '../types';
import SwimLaneCalendar from '../components/SwimLaneCalendar';

// ─── Event-type farver (læses fra CSS-variabler defineret i index.css) ────────
function cssVar(name: string, fallback: string) {
  if (typeof window === 'undefined') return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

const COLOR = {
  booking:    cssVar('--cal-booking',    '#7c3aed'),
  service:    cssVar('--cal-service',    '#16a34a'),
  inspection: cssVar('--cal-inspection', '#2563eb'),
  workshop:   cssVar('--cal-workshop',   '#ea580c'),
  checkup:    cssVar('--cal-checkup',    '#0891b2'),
};

// ─── Kombineret kalender-event ───────────────────────────────────────────────
type CalEvent =
  | { kind: 'booking';    booking: Booking }
  | { kind: 'service';    vehicle: Vehicle }
  | { kind: 'inspection'; vehicle: Vehicle }
  | { kind: 'workshop';   event: FleetEvent }
  | { kind: 'checkup';    event: FleetEvent };

type CalKind = CalEvent['kind'];

function calEventId(e: CalEvent) {
  if (e.kind === 'booking')   return `b-${e.booking.id}`;
  if (e.kind === 'service')   return `s-${e.vehicle.id}`;
  if (e.kind === 'workshop')  return `w-${e.event.id}`;
  if (e.kind === 'checkup')   return `c-${e.event.id}`;
  return `i-${e.vehicle.id}`;
}

function calEventDate(e: CalEvent): string {
  if (e.kind === 'booking')   return e.booking.starts_at;
  if (e.kind === 'service')   return e.vehicle.next_service!;
  if (e.kind === 'workshop')  return e.event.date;
  if (e.kind === 'checkup')   return e.event.date;
  return e.vehicle.next_inspection!;
}

function calEventReg(e: CalEvent): string {
  if (e.kind === 'booking')                          return e.booking.registration_number;
  if (e.kind === 'workshop' || e.kind === 'checkup') return e.event.registration_number;
  return e.vehicle.registration_number ?? '';
}

import { parseUtc } from '../utils/dates';

function fmtDateTime(d: string, tz: string) {
  return parseUtc(d).toLocaleString('da-DK', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: tz,
  });
}

function fmtDateHeading(dateStr: string) {
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('da-DK', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

function dateKey(d: string) { return d.slice(0, 10); }

// ─── Event-type label (via i18n) ──────────────────────────────────────────────
const KIND_KEYS: Record<CalKind, string> = {
  booking: 'calendar.rental', service: 'calendar.service',
  inspection: 'calendar.inspection', workshop: 'calendar.workshop', checkup: 'calendar.checkup',
};

// ─── Detail-panel ─────────────────────────────────────────────────────────────
function EventDetail({ event, onClose, tz }: { event: CalEvent; onClose: () => void; tz: string }) {
  const { t } = useTranslation();
  return (
    <div className="card p-5 space-y-3">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full shrink-0" style={{ background: COLOR[event.kind] }} />
          <span className="reg-number text-base">{calEventReg(event)}</span>
        </span>
        <button onClick={onClose} className="modal-close">&times;</button>
      </div>
      <div className="divide-y text-sm">
        {event.kind === 'booking' && (
          <>
            <DetailRow label="Periode"  value={`${fmtDateTime(event.booking.starts_at, tz)} → ${fmtDateTime(event.booking.ends_at, tz)}`} />
            {event.booking.renter_name  && <DetailRow label="Lejer"   value={event.booking.renter_name} />}
            {event.booking.renter_phone && <DetailRow label="Telefon" value={event.booking.renter_phone} />}
            {event.booking.renter_email && <DetailRow label="E-mail"  value={event.booking.renter_email} />}
            {event.booking.notes          && <DetailRow label="Noter"   value={event.booking.notes} />}
          </>
        )}
        {event.kind === 'service' && (
          <DetailRow label="Dato" value={fmtDateTime(event.vehicle.next_service!, tz)} />
        )}
        {event.kind === 'inspection' && (
          <DetailRow label="Dato" value={fmtDateTime(event.vehicle.next_inspection!, tz)} />
        )}
        {(event.kind === 'workshop' || event.kind === 'checkup') && (
          <>
            <DetailRow label="Dato" value={fmtDateTime(event.event.date, tz)} />
            {event.kind === 'workshop' && event.event.description && <DetailRow label="Beskrivelse" value={event.event.description} />}
            {event.kind === 'workshop' && event.event.workshop     && <DetailRow label="Værksted"    value={event.event.workshop} />}
          </>
        )}
      </div>
      <p className="text-xs text-ink-300">
        {t(KIND_KEYS[event.kind])}
      </p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="py-2 grid grid-cols-[6rem_1fr] gap-2">
      <span className="text-ink-500">{label}</span>
      <span className="font-medium text-ink-900">{value}</span>
    </div>
  );
}

// ─── Listevisning ─────────────────────────────────────────────────────────────
function ListView({ calEvents, onSelect, tz, from, to }: {
  calEvents: CalEvent[];
  onSelect: (e: CalEvent) => void;
  tz: string;
  from: string;
  to: string;
}) {
  const { t } = useTranslation();
  const groups = new Map<string, CalEvent[]>();
  const addToDay = (key: string, e: CalEvent) => {
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(e);
  };

  for (const e of calEvents) {
    if (e.kind === 'booking') {
      const startKey = dateKey(e.booking.starts_at);
      const endKey   = dateKey(e.booking.ends_at);
      // Only show if booking overlaps with [from, to]
      if (startKey > to || endKey < from) continue;
      const cur = new Date(Math.max(new Date(startKey).getTime(), new Date(from).getTime()));
      const end = new Date(Math.min(new Date(endKey).getTime(), new Date(to).getTime()));
      while (cur <= end) {
        addToDay(cur.toISOString().slice(0, 10), e);
        cur.setDate(cur.getDate() + 1);
      }
    } else {
      const key = dateKey(calEventDate(e));
      if (key >= from && key <= to) addToDay(key, e);
    }
  }

  const sorted = [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));

  if (sorted.length === 0) {
    return <p className="text-sm text-ink-500 text-center py-12">{t('calendar.no_events')}</p>;
  }

  return (
    <div className="space-y-6">
      {sorted.map(([date, dayEvents]) => (
        <div key={date}>
          <h3 className="text-sm font-semibold text-ink-500 uppercase tracking-wide mb-2 capitalize">
            {fmtDateHeading(date)}
          </h3>
          <div className="list-card divide-y divide-cream-100">
            {dayEvents.map(e => {
              const reg   = calEventReg(e);
              const label = e.kind === 'booking'
                ? `${fmtDateTime(e.booking.starts_at, tz)} → ${fmtDateTime(e.booking.ends_at, tz)}`
                : t(KIND_KEYS[e.kind]);
              const sub = e.kind === 'booking' && e.booking.renter_name
                ? e.booking.renter_name
                : e.kind === 'workshop' && e.event.description
                  ? e.event.description
                  : '';
              return (
                <button
                  key={calEventId(e) + date}
                  onClick={() => onSelect(e)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-cream-100 text-left transition-colors"
                >
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLOR[e.kind] }} />
                  <span className="reg-number text-sm w-20 shrink-0">{reg}</span>
                  <span className="text-sm text-ink-500 shrink-0">{label}</span>
                  {sub && <span className="text-sm text-ink-700 truncate">{sub}</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Filterlinje ──────────────────────────────────────────────────────────────
const ALL_KINDS: CalKind[] = ['booking', 'service', 'inspection', 'workshop', 'checkup'];

interface FilterBarProps {
  activeKinds: Set<CalKind>;
  onToggleKind: (k: CalKind) => void;
  regFilter: string;
  onRegFilter: (r: string) => void;
  listFrom?: string;
  listTo?: string;
  onListFrom?: (v: string) => void;
  onListTo?: (v: string) => void;
}

function FilterBar({ activeKinds, onToggleKind, regFilter, onRegFilter, listFrom, listTo, onListFrom, onListTo }: FilterBarProps) {
  const { t } = useTranslation();
  const [input, setInput]             = useState(regFilter);
  const [suggestions, setSuggestions] = useState<{ id: number; registration_number: string }[]>([]);
  const [regOpen, setRegOpen]         = useState(false);
  const [kindOpen, setKindOpen]       = useState(false);
  const regRef  = useRef<HTMLDivElement>(null);
  const kindRef = useRef<HTMLDivElement>(null);

  // Sync external clear
  useEffect(() => {
    if (!regFilter) { setInput(''); setSuggestions([]); }
  }, [regFilter]);

  const search = useCallback(async (q: string) => {
    if (q.length < 1) { setSuggestions([]); setRegOpen(false); return; }
    const hits = await searchVehicles(q, 'active');
    setSuggestions(hits);
    setRegOpen(hits.length > 0);
  }, []);

  const handleChange = (val: string) => {
    setInput(val);
    if (!val) { onRegFilter(''); setSuggestions([]); setRegOpen(false); return; }
    search(val);
  };

  const pick = (reg: string) => {
    setInput(reg);
    onRegFilter(reg);
    setSuggestions([]);
    setRegOpen(false);
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (ev: MouseEvent) => {
      if (regRef.current  && !regRef.current.contains(ev.target as Node))  setRegOpen(false);
      if (kindRef.current && !kindRef.current.contains(ev.target as Node)) setKindOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allActive = activeKinds.size === ALL_KINDS.length;
  const kindLabel = allActive
    ? t('calendar.all_types')
    : activeKinds.size === 1
      ? t(KIND_KEYS[[...activeKinds][0]])
      : `${activeKinds.size} / ${ALL_KINDS.length}`;

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {/* Begivenhedstype-dropdown */}
      <div ref={kindRef} className="relative flex items-center gap-1.5">
        <span className="text-xs text-ink-500 font-medium">Type:</span>
        <button
          onClick={() => setKindOpen(o => !o)}
          className="filter-input flex items-center gap-1.5 text-ink-700"
        >
          <span className="flex items-center gap-1.5">
            {!allActive && [...activeKinds].map(k => (
              <span key={k} className="w-2 h-2 rounded-full shrink-0" style={{ background: COLOR[k] }} />
            ))}
            {kindLabel}
          </span>
          <svg className="w-4 h-4 text-ink-300 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </button>
        {kindOpen && (
          <div className="absolute z-20 mt-1 left-8 w-52 bg-cream-50 border border-cream-100 rounded-lg shadow-card-md py-1">
            <label className="flex items-center gap-2.5 px-3 py-2 hover:bg-cream-100 cursor-pointer border-b border-cream-100">
              <input
                type="checkbox"
                checked={allActive}
                onChange={() => ALL_KINDS.forEach(k => {
                  const shouldBeActive = !allActive;
                  if (activeKinds.has(k) !== shouldBeActive) onToggleKind(k);
                })}
                className="rounded accent-sage-700"
              />
              <span className="text-sm font-medium text-ink-700">{t('calendar.all_types')}</span>
            </label>
            {ALL_KINDS.map(kind => (
              <label key={kind} className="flex items-center gap-2.5 px-3 py-2 hover:bg-cream-100 cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeKinds.has(kind)}
                  onChange={() => onToggleKind(kind)}
                  className="rounded accent-sage-700"
                />
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLOR[kind] }} />
                <span className="text-sm text-ink-700">{t(KIND_KEYS[kind])}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Separator */}
      <div className="w-px h-4 bg-ink-100 hidden sm:block mx-1" />

      {/* Periode-filter (kun listevisning) */}
      {listFrom !== undefined && onListFrom && onListTo && (
        <>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-ink-500 font-medium">{t('calendar.from')}</span>
            <input
              type="date"
              value={listFrom}
              onChange={e => onListFrom(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-ink-500 font-medium">{t('calendar.to')}</span>
            <input
              type="date"
              value={listTo}
              min={listFrom}
              onChange={e => onListTo(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="w-px h-4 bg-ink-100 hidden sm:block mx-1" />
        </>
      )}

      {/* Bil-filter med autocomplete */}
      <div ref={regRef} className="relative flex items-center gap-1.5">
        <span className="text-xs text-ink-500 font-medium">{t('calendar.vehicle_label')}</span>
        <span className="inline-flex items-center gap-1">
          <input
            type="text"
            value={input}
            onChange={e => handleChange(e.target.value.toUpperCase())}
            onFocus={() => suggestions.length > 0 && setRegOpen(true)}
            placeholder={t('calendar.plate_placeholder')}
            className="filter-input w-32 uppercase font-mono placeholder:normal-case placeholder:font-sans placeholder:text-[#a89d8a]"
          />
          {regFilter && (
            <button
              onClick={() => { setInput(''); onRegFilter(''); setSuggestions([]); }}
              className="text-ink-300 hover:text-ink-700 p-1 rounded hover:bg-cream-100"
              title={t('calendar.clear')}
            >
              <X size={13} />
            </button>
          )}
        </span>
        {regOpen && (
          <ul className="absolute z-20 mt-1 left-8 w-48 bg-cream-50 border border-cream-100 rounded-lg shadow-card-md overflow-hidden text-sm">
            {suggestions.map(s => (
              <li key={s.id}>
                <button
                  onMouseDown={() => pick(s.registration_number)}
                  className="w-full text-left px-3 py-2 hover:bg-cream-100 font-mono font-semibold text-ink-900"
                >
                  {s.registration_number}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── Dato + tid med 15-min intervaller ───────────────────────────────────────
const HOURS    = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const QUARTERS = ['00', '15', '30', '45'];

function DateTimePicker({ value, onChange, minDate }: {
  value: string;
  onChange: (v: string) => void;
  minDate?: string;
}) {
  const [datePart, timePart] = value.split('T');
  const [hh, mm] = (timePart ?? '00:00').split(':');
  const minuteVal = QUARTERS.includes(mm) ? mm : '00';

  const emit = (d: string, h: string, m: string) => onChange(`${d}T${h}:${m}`);

  return (
    <div className="flex items-center gap-2">
      <input
        type="date"
        value={datePart}
        min={minDate}
        onChange={e => emit(e.target.value, hh, minuteVal)}
        className="filter-input"
      />
      <div className="flex items-center border border-cream-100 rounded-lg bg-cream-50 text-sm">
        <select
          value={hh}
          onChange={e => emit(datePart, e.target.value, minuteVal)}
          className="px-2 py-1.5 bg-cream-50 text-ink-900 focus:outline-none border-r border-cream-100 rounded-l-lg cursor-pointer appearance-none"
        >
          {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
        </select>
        <span className="px-1 text-ink-300 select-none">:</span>
        <select
          value={minuteVal}
          onChange={e => emit(datePart, hh, e.target.value)}
          className="px-2 py-1.5 bg-cream-50 text-ink-900 focus:outline-none rounded-r-lg cursor-pointer appearance-none"
        >
          {QUARTERS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
    </div>
  );
}

// ─── Ledige biler ────────────────────────────────────────────────────────────
function AvailablePanel({ vehicles, bookings, blocks }: { vehicles: Vehicle[]; bookings: Booking[]; blocks: VehicleBlock[] }) {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const nowLocal = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:00`;
  const [from, setFrom] = useState(nowLocal);
  const [to, setTo]     = useState(nowLocal);

  const effectiveTo = to < from ? from : to;

  // MySQL returnerer DATETIME som "2026-05-21 16:00:00" (mellemrum),
  // DateTimePicker giver "2026-05-21T08:00" (T). String-sammenligning af de
  // to formater er upålidelig på samme dag — derfor Date-objekter.
  const fromTs = new Date(from        + ':00').getTime();
  const toTs   = new Date(effectiveTo + ':00').getTime();

  // Tider fra API'et er UTC (parseUtc). Før blev de læst som dansk tid, og en booking kunne
  // ligge 2 timer forkert i forhold til det valgte tidsrum.
  const overlaps = (s: string, e: string) =>
    parseUtc(s).getTime() < toTs && parseUtc(e).getTime() > fromTs;

  const freeVehicles = vehicles.filter(v => {
    const booked = bookings.some(b =>
      !b.cancelled && b.registration_number === v.registration_number && overlaps(b.starts_at, b.ends_at));
    // Værksted, syn o.l. markeret "blokeret for udlejning" gør bilen lige så optaget
    const blocked = blocks.some(b => b.registration_number === v.registration_number && overlaps(b.starts_at, b.ends_at));
    return !booked && !blocked;
  });

  const busyCount = vehicles.length - freeVehicles.length;

  return (
    <div className="list-card p-5 mb-4">
      <div className="flex flex-wrap items-end gap-4 mb-5">
        <div>
          <label className="label">Fra</label>
          <DateTimePicker value={from} onChange={setFrom} />
        </div>
        <div>
          <label className="label">Til</label>
          <DateTimePicker value={effectiveTo} onChange={setTo} minDate={from.slice(0, 10)} />
        </div>
        <p className="text-sm text-ink-500 pb-2">
          <span className="font-semibold text-sage-700">{freeVehicles.length} ledig{freeVehicles.length !== 1 ? 'e' : ''}</span>
          {busyCount > 0 && <span className="text-ink-300"> · {busyCount} optaget</span>}
          <span className="text-ink-300"> af {vehicles.length} aktive biler</span>
        </p>
      </div>

      {freeVehicles.length === 0 ? (
        <p className="text-sm text-ink-500 text-center py-8">Ingen ledige biler i perioden</p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-2">
          {freeVehicles.map(v => (
            <div key={v.id} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-sage-300 bg-sage-100">
              <span className="w-2 h-2 rounded-full bg-sage-700 shrink-0" />
              <div className="min-w-0">
                <p className="reg-number text-sm">{v.registration_number}</p>
                {(v.make || v.model) && (
                  <p className="text-xs text-ink-500 truncate">{[v.make, v.model].filter(Boolean).join(' ')}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Hoved-komponent ──────────────────────────────────────────────────────────
type ViewMode = 'calendar' | 'list';

export default function CalendarPage() {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const tz = settings.timezone || 'Europe/Copenhagen';
  const [bookings, setBookings]       = useState<Booking[]>([]);
  const [blocks, setBlocks]           = useState<VehicleBlock[]>([]);
  const [vehicles, setVehicles]       = useState<Vehicle[]>([]);
  const [fleetEvents, setFleetEvents] = useState<FleetEvent[]>([]);
  const [selected, setSelected]       = useState<CalEvent | null>(null);
  const [viewMode, setViewMode]       = useState<ViewMode>('calendar');
  const [syncing, setSyncing]         = useState(false);
  const [syncMsg, setSyncMsg]         = useState<string | null>(null);
  const [syncErr, setSyncErr]         = useState<string | null>(null);
  // Hentes fra serveren, så den også viser den timelige automatiske sync — ikke kun knappen
  const [lastSynced, setLastSynced]   = useState<string | null>(null);

  // Filtre
  const [activeKinds, setActiveKinds] = useState<Set<CalKind>>(new Set(ALL_KINDS));
  const [regFilter, setRegFilter]     = useState('');
  const [showAvailable, setShowAvailable] = useState(false);

  // Liste-periode (default: i dag → én måned frem)
  const defaultFrom = new Date().toISOString().slice(0, 10);
  const defaultTo   = (() => {
    const d = new Date(); d.setMonth(d.getMonth() + 1);
    return d.toISOString().slice(0, 10);
  })();
  const [listFrom, setListFrom] = useState(defaultFrom);
  const [listTo,   setListTo]   = useState(defaultTo);

  const load = async () => {
    const [b, v, fe, st, bl] = await Promise.all([
      getBookings(),
      getVehicles({ limit: '999', status: 'active' }).then(r => r.data),
      getFleetEvents(),
      getSyncStatus().catch(() => ({ last_synced: null })),
      getVehicleBlocks().catch(() => [] as VehicleBlock[]),
    ]);
    setBookings(b);
    setVehicles(v);
    setFleetEvents(fe);
    setBlocks(bl);
    setLastSynced(st.last_synced);
  };

  useEffect(() => { load(); }, []);

  const handleSync = async () => {
    setSyncing(true); setSyncMsg(null); setSyncErr(null);
    try {
      const r = await syncCalendar();
      setSyncMsg(`${r.created} nye · ${r.updated} opdaterede · ${r.cancelled} aflyst`);
      await load();  // henter også det nye sync-tidspunkt
    } catch (e) {
      setSyncErr(e instanceof Error ? e.message : 'Ukendt fejl');
    }
    setSyncing(false);
  };


  const toggleKind = (k: CalKind) => {
    setActiveKinds(prev => {
      const next = new Set(prev);
      if (next.has(k)) {
        if (next.size === 1) return prev; // behold mindst én aktiv
        next.delete(k);
      } else {
        next.add(k);
      }
      return next;
    });
  };

  // Byg kombineret event-liste
  const allCalEvents: CalEvent[] = [
    ...bookings.map(b => ({ kind: 'booking' as const, booking: b })),
    ...vehicles.filter(v => v.next_service).map(v => ({ kind: 'service' as const, vehicle: v })),
    ...vehicles.filter(v => v.next_inspection).map(v => ({ kind: 'inspection' as const, vehicle: v })),
    ...fleetEvents.filter(e => e.event_type === 'maintenance').map(e => ({ kind: 'workshop' as const, event: e })),
    ...fleetEvents.filter(e => e.event_type === 'checkup').map(e => ({ kind: 'checkup' as const, event: e })),
  ];

  // Anvend filtre
  const calEvents = allCalEvents.filter(e => {
    if (!activeKinds.has(e.kind)) return false;
    if (regFilter && calEventReg(e).toUpperCase() !== regFilter.toUpperCase()) return false;
    return true;
  });

  return (
    <main className="max-w-7xl mx-auto px-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h2 className="page-title">{t('nav.calendar')}</h2>
          <div className="flex gap-1">
            <button onClick={() => setViewMode('calendar')} className={`btn-toggle ${viewMode === 'calendar' ? 'btn-toggle-active' : 'btn-toggle-inactive'}`}>{t('calendar.view_calendar')}</button>
            <button onClick={() => setViewMode('list')}     className={`btn-toggle ${viewMode === 'list'     ? 'btn-toggle-active' : 'btn-toggle-inactive'}`}>{t('calendar.view_list')}</button>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {syncMsg && <span className="text-sm text-[var(--color-success)]">{syncMsg}</span>}
          {syncErr && <span className="text-sm text-[var(--color-error)]">{syncErr}</span>}
          <button
            onClick={() => setShowAvailable(v => !v)}
            className={`btn ${showAvailable ? 'btn-primary' : 'btn-secondary'}`}
          >
            {t('calendar.show_available')}
          </button>
          {settings.gomore_ical_url && (
            <div className="flex flex-col items-end gap-0.5">
              <button onClick={handleSync} disabled={syncing} className="btn btn-primary">
                {syncing ? '...' : t('calendar.sync')}
              </button>
              {lastSynced && (
                <span className="text-xs text-ink-300">
                  Sidst synkroniseret {new Date(lastSynced).toLocaleString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Ledige biler */}
      {showAvailable && <AvailablePanel vehicles={vehicles} bookings={bookings} blocks={blocks} />}

      {/* Filterlinje */}
      <FilterBar
        activeKinds={activeKinds}
        onToggleKind={toggleKind}
        regFilter={regFilter}
        onRegFilter={setRegFilter}
        {...(viewMode === 'list' ? {
          listFrom, listTo,
          onListFrom: setListFrom,
          onListTo:   setListTo,
        } : {})}
      />

      {viewMode === 'list' ? (
        <ListView calEvents={calEvents} onSelect={setSelected} tz={tz} from={listFrom} to={listTo} />
      ) : (
        <SwimLaneCalendar
          vehicles={vehicles.filter(v => !regFilter || v.registration_number?.toUpperCase() === regFilter.toUpperCase())}
          bookings={bookings}
          blocks={blocks}
          onSelectBooking={(b) => setSelected({ kind: 'booking', booking: b })}
        />
      )}

      {/* Lejedetaljer som modal — bruges fra både liste og swim lane */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-cream-50 rounded-card shadow-card-md max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <EventDetail event={selected} onClose={() => setSelected(null)} tz={tz} />
          </div>
        </div>
      )}
    </main>
  );
}
